/**
 * EQuIP Scope Query Hooks
 *
 * React Query hooks for scope selection endpoints: manager and clinician lists.
 * Wraps equipApi.scope.* with typed key factories and enabled guards
 * for conditional fetching.
 */

import { useQuery } from '@tanstack/react-query'
import { equipApi } from '@/services/equipApi'
import type { ManagerOption, ClinicianOption, SupervisingPhysicianOption } from '@/lib/equip-types'

// ---------------------------------------------------------------------------
// Key factory
// ---------------------------------------------------------------------------

export const equipScopeKeys = {
  all: () => ['equip', 'scope'] as const,
  managers: () => ['equip', 'scope', 'managers'] as const,
  clinicians: () => ['equip', 'scope', 'clinicians'] as const,
  supervisingPhysicians: () => ['equip', 'scope', 'supervising-physicians'] as const,
  selfIsProvider: (year?: number, quarter?: number) =>
    ['equip', 'scope', 'self-is-provider', year, quarter] as const,
  seniorLeads: () => ['equip', 'scope', 'senior-leads'] as const,
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches the list of managers available for scope selection.
 * Query is controlled by the enabled flag — pass false to prevent fetching.
 */
export function useEquipManagers(enabled: boolean) {
  return useQuery<ManagerOption[]>({
    queryKey: equipScopeKeys.managers(),
    queryFn: () => equipApi.scope.getManagers(),
    enabled,
  })
}

/**
 * Fetches the list of clinicians available for scope selection.
 * Query is controlled by the enabled flag — pass false to prevent fetching.
 */
export function useEquipClinicians(enabled: boolean) {
  return useQuery<ClinicianOption[]>({
    queryKey: equipScopeKeys.clinicians(),
    queryFn: () => equipApi.scope.getClinicians(),
    enabled,
  })
}

/**
 * Checks whether the authenticated user has their own EQuIP performance data.
 * Only fetched for leadership users (showClinicianDropdown=true) — pure equip.read
 * users already take the providerEmail=null path and don't need this check.
 */
export function useEquipSelfIsProvider(enabled: boolean, year?: number, quarter?: number) {
  return useQuery<{ is_provider: boolean }>({
    queryKey: equipScopeKeys.selfIsProvider(year, quarter),
    queryFn: () => equipApi.scope.getSelfIsProvider(year && quarter ? { year, quarter } : undefined),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetches the list of supervising physicians for admin impersonation on the APC Dashboard.
 * Query is controlled by the enabled flag — pass false (non-admin) to prevent fetching.
 */
export function useEquipSupervisingPhysicians(enabled: boolean) {
  return useQuery<SupervisingPhysicianOption[]>({
    queryKey: equipScopeKeys.supervisingPhysicians(),
    queryFn: () => equipApi.dashboard.getSupervisingPhysicians(),
    enabled,
  })
}

export interface SeniorLeadOption {
  senior_lead_email: string
  senior_lead_name: string
}

/**
 * Fetches the list of senior leads (RDCOEs) for the admin RDCOE picker
 * on the Senior Leadership page. Only fetched when enabled (admin users).
 */
export function useAvailableSeniorLeads(enabled: boolean) {
  return useQuery<SeniorLeadOption[]>({
    queryKey: equipScopeKeys.seniorLeads(),
    queryFn: () => equipApi.scope.getSeniorLeads(),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}
